/* eslint-disable no-underscore-dangle */
import {
  Component,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
  ViewChild
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import {
  BehaviorSubject, combineLatest, Observable,
  Subscription
} from 'rxjs';
import {
  filter,
  map,
  shareReplay,
  skip,
  switchMap,
  take,
  takeUntil
} from 'rxjs/operators';

import { MonolithClinicalAssessmentsHttpService } from '@sondermind/data-access/assessments-monolith';
import { CHAT_STORE, IChatStore } from '@sondermind/data-access/chat';
import { PatientService } from '@sondermind/data-access/patient';
import { InternalReferralService } from '@sondermind/data-access/provider-facing/internal-referral';
import { CalendarEventsHttpService, UpcomingSessionService } from '@sondermind/data-access/scheduling';
import { SondermindApplicationService } from '@sondermind/data-access/sondermind-app-service';
import { Logger } from '@sondermind/datadog-logging';
import { ConfirmationModalComponent } from '@sondermind/features/modals/confirmation';

import { IrisButtonIconPositions, IrisButtonTypes } from '@sondermind/iris/button';
import { IconColors, IconSizes, IconUsageTypes, irisIcon } from '@sondermind/iris/icon';
import { IrisModalService } from '@sondermind/iris/modal';
import { IrisTooltipPosition } from '@sondermind/iris/tooltip';
import { ITabItem } from '@sondermind/iris/tab';

import { SondermindEvent, SondermindEvents } from '@sondermind/analytics';
import { SegmentService } from '@sondermind/segment';
import { AssessmentLinkLocations } from '@sondermind/utilities/models-assessments-monolith';

import { InternalReferralEligibility, Patient } from '@sondermind/utilities/models-patient';
import { SMCalendarEvent } from '@sondermind/utilities/models-scheduling';
import { ProviderUser } from '@sondermind/utilities/models-user-provider';

import { ChartRiskFlagReviewService, ClientDetailPageService } from '@sondermind/features/provider-facing/client-detail';
import { InternalReferralTypes } from '@sondermind/utilities/models-referrals';
import { IrisIconButtonTypes } from '@sondermind/iris/icon-button';
import { NotificationToastService, ToastType } from '@sondermind/features/notification-toast';
import { IMenuItemList, IrisMenuComponent } from '@sondermind/iris/menu';
import { RouteData } from '@sondermind/utilities/services';
import { InfoLinks } from '@sondermind/core/enums/info-links.enum';

import { ClientDetailsSmartNotesComponent } from '@sondermind/member/components/client-detail-smart-notes/client-detail-smart-notes.component';
import { ClientDetailTreatmentplanComponent } from '@sondermind/member/components/client-detail-treatmentplan/client-detail-treatmentplan.component';
import { ClientActivation, ClientTerminationReasonsModalService } from '../../services/client-termination-reasons/client-termination-reasons-modal.service';
import { MatchClientStore } from '../../stores/match-client.store';
import { PsychiatryModalComponent, PsychiatryModalInputType } from '../psychiatry/psychiatry-modal.component';

export const THERAPY_UNDERAGE_WARNING_TEXT = 'You may need to obtain consent from a parent or legal guardian.';
export const PSYCH_UNDERAGE_WARNING_TEXT = 'You must obtain consent from a parent or legal guardian.';

@Component({
  selector: 'member-page-client-detail',
  templateUrl: './client-detail.component.html',
  styleUrls: ['./client-detail.component.scss'],
  providers: [
    ClientDetailPageService,
    // Page-scoped so the header's risk-flag banner and the journey shell (a routed child) share one
    // instance: the banner posts a review request here, the shell consumes it.
    ChartRiskFlagReviewService
  ],
  standalone: false
})
export class ClientDetailPage implements OnInit, OnDestroy {
  @ViewChild(ConfirmationModalComponent)
    confirmModal: ConfirmationModalComponent;
  readonly actionListMenu = viewChild<IrisMenuComponent>('actionListMenu');

  private destroyed$ = new EventEmitter<void>();

  modalVisible: boolean = false;
  loadingStatus: string = 'Loading please wait ....';
  loading: boolean;
  clients: Patient[] = [];

  user: ProviderUser;
  patient: Patient;
  userSub: Subscription;
  routeSub: Subscription;
  activeSub: Subscription;
  patientSub: Subscription;
  assessmentLinkLocations = AssessmentLinkLocations;
  helperText: string;
  patientAge: number;
  patientUnderAge: boolean;
  underageWarningText: string = THERAPY_UNDERAGE_WARNING_TEXT;
  patientActive: boolean;
  status: string;
  accountIncomplete: boolean = false;
  allowScheduling: boolean = false;
  patientLocation: string;
  patientName: string;
  patientLegalName: string;
  isMatchList: boolean;
  breadcrumb: string;
  timezone: string;
  zoneAbbr: string;
  sessionInfo$: Observable<{ next: SMCalendarEvent; prev: SMCalendarEvent; totalCount: number; }>;
  isLoading: boolean = false;
  internaleReferralEnabled$: Observable<boolean>;
  displayReferButton$ = new BehaviorSubject<boolean>(false);
  IconColors = IconColors;
  IconUsageTypes = IconUsageTypes;
  IconSizes = IconSizes;
  IrisButtonTypes = IrisButtonTypes;
  IrisIconButtonTypes = IrisIconButtonTypes;
  IrisButtonIconPositions = IrisButtonIconPositions;
  IrisTooltipPosition = IrisTooltipPosition;
  eligibilityData: InternalReferralEligibility;
  tfaFormLink = InfoLinks.REFER_TO_PSYCHIATRY;
  manageClientLabel = 'Refer to psychiatry / Manage client';
  usePrescriptions = false;
  tabItems: ITabItem[] = [
    {
      title: 'Session notes',
      icon: 'chair',
      iconActive: 'chair_filled',
      dataTest: 'tab-session-notes',
      route: './smart_notes'
    },
    {
      title: 'Client journey',
      icon: 'client_journey_tab',
      iconActive: 'client_journey_tab',
      dataTest: 'tab-client-journey',
      route: './client_journey'
    },
    {
      title: 'Assessments',
      icon: 'quiz',
      iconActive: 'quiz_filled',
      dataTest: 'tab-assessments',
      route: './assessments',
      queryParams: { link_tracking: true, link_location: AssessmentLinkLocations.CLIENT_LIST }
    },
    { title: 'Documents',
      icon: 'folder_open_outline',
      iconActive: 'folder_open_filled',
      dataTest: 'tab-documents',
      route: './documents' },
    { title: 'Payment',
      icon: 'credit_card',
      iconActive: 'credit_card_filled',
      dataTest: 'tab-payment-info',
      route: './payment_info' },
    { title: 'Contact',
      icon: 'contact_phone_outline',
      iconActive: 'contact_phone_filled',
      dataTest: 'tab-contact-info',
      route: './contact_info' },
  ];
  isEligibleForPsychiatry: boolean = false;
  referToLabel: string;
  referToIcon: irisIcon;
  modalOpen = false;
  showClientHeader = true;
  readonly menuItems = signal<IMenuItemList[]>([]);
  routeComingFrom = '';

  constructor(
    private activatedRoute: ActivatedRoute,
    private appService: SondermindApplicationService,
    private calendarEventService: CalendarEventsHttpService,
    private clientDetailPageService: ClientDetailPageService,
    private clientTerminationReasonsModalService: ClientTerminationReasonsModalService,
    private monolithAssessmentsHttp: MonolithClinicalAssessmentsHttpService,
    private matchClientStore: MatchClientStore,
    public _patientService: PatientService,
    private router: Router,
    private routeData: RouteData,
    private notificationService: NotificationToastService,
    private upcomingSessionService: UpcomingSessionService,
    @Inject(CHAT_STORE)
    private chatStore: IChatStore,
    private irisModalService: IrisModalService,
    public internalReferralService: InternalReferralService,
    private segmentService: SegmentService<SondermindEvents>,
    private logger: Logger,
  ) {
    this.activatedRoute.queryParams.pipe(takeUntil(this.destroyed$)).subscribe((params) => {
      this.isMatchList = params?.matchList === 'true';
      if (this.isMatchList) {
        this.breadcrumb = 'Scheduled referrals';
      } else {
        this.breadcrumb = 'Active clients ';
      }
    });
  }

  ngOnInit(): void {
    this.routeComingFrom = this.routeData.getPreviousUrl();

    this.userSub = this.appService.currentUser$.pipe(takeUntil(this.destroyed$)).subscribe((u) => {
      this.user = u;
      if (u.psychiatrist && u.ePrescriptionEnabled) {
        this.usePrescriptions = true;
        if (this.usePrescriptions) {
          this.tabItems.push({
            title: 'Prescriptions',
            dataTest: 'tab-prescriptions-info',
            route: './prescriptions_info',
            icon: 'prescription',
            iconActive: 'prescription_filled',
          });
        }
      }
    });

    this.routeSub = this.activatedRoute.params.pipe(takeUntil(this.destroyed$)).subscribe((params) => {
      this.clientDetailPageService.loadPatient(params.id);
    });

    this.setPatient();

    this.patientSub = this.clientDetailPageService.patient.pipe(
      map((p) => p && p.dateOfBirth),
      takeUntil(this.destroyed$)
    )
      .subscribe((p) => {
        if (p) {
          this.patientAge = this.patient.age(this.appService.currentUser.timeZone);
          this.patientUnderAge = this.patientAge < 18;
          this.updateUnderageWarningText();
          this.timezone = this.appService.currentUser.timeZone;
          this.zoneAbbr = this.appService.zoneAbbr;
        }
      });

    this.activeSub = this.clientDetailPageService.patient.pipe(
      map((p) => p && p.active),
      takeUntil(this.destroyed$)
    )
      .subscribe((p) => {
        if (p) {
          this.patientActive = p;
        }
      });

    this.initializeSessionInfo();

    if (!this.appService.currentUser.psychiatrist) {
      this.enableTreatmentPlanTab();
    }
  }

  updateUnderageWarningText(): void {
    this.underageWarningText = this.appService.currentUser.psychiatrist
      ? PSYCH_UNDERAGE_WARNING_TEXT
      : THERAPY_UNDERAGE_WARNING_TEXT;
  }

  private enableTreatmentPlanTab(): void {
    // Check if treatment plan tab already exists to prevent duplicates
    const treatmentPlanTabExists = this.tabItems.some((tab) => tab.title === 'Treatment plan');
    if (!treatmentPlanTabExists) {
      const assessmentsIndex = this.tabItems.findIndex((tab) => tab.title === 'Assessments');
      const insertAt = assessmentsIndex >= 0 ? assessmentsIndex + 1 : 2;
      this.tabItems.splice(insertAt, 0, {
        title: 'Treatment plan',
        icon: 'psychiatry',
        iconActive: 'psychiatry_filled',
        dataTest: 'tab-treatment-plan',
        route: './treatment_plan'
      });
    }
  }

  private initializeSessionInfo(): void {
    const getSessionData$ = (patient: Patient) => {
      const nextSession$ = this.upcomingSessionService.nextUpcomingSession$(patient.id);

      /* HACK: Currently we do not have a need to create a store simply for previous sessions,
      so here we make the service call to grab the previous session directly in this component.
      In the future if a session store exists we should refactor this to instead use said store. */
      const prevSession$ = this.calendarEventService.index({
        filter: [
          { key: 'for_patient', value: patient.id.toString() },
          { key: 'not_canceled', value: 'true' },
          { key: 'occurs_before', value: new Date().toISOString() }
        ],
        page: { number: 1, size: 1 },
        sort: [{ key: 'start_time', asc: false }]
      }).pipe(
        map((resp) => resp.data?.[0]),
        shareReplay({ refCount: true, bufferSize: 1 })
      );

      const totalSessionCount$ = this.monolithAssessmentsHttp.getTotalSessions$(patient.personaClientId).pipe(
        map((res) => res?.[0]?.totalSessions ?? 0)
      );

      return [nextSession$, prevSession$, totalSessionCount$];
    };

    this.sessionInfo$ = this.clientDetailPageService.patient.pipe(
      filter((patient): patient is Patient => !!patient && !!patient.id && !!patient.personaClientId),
      switchMap((patient) => combineLatest(getSessionData$(patient)).pipe(
        map(
          ([next, prev, totalCount]: [SMCalendarEvent, SMCalendarEvent, number]) => ({ next, prev, totalCount })
        )
      )),
      shareReplay({ refCount: true, bufferSize: 1 })
    );
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  setPatient(): void {
    this.isLoading = true;
    this.clientDetailPageService.patient.pipe(
      skip(1),
      takeUntil(this.destroyed$)
    ).subscribe((patient) => {
      this.patient = patient;
      this.patientName = patient.displayName;
      this.patientLegalName = `${patient.firstName} ${patient.lastName}`;
      this.allowScheduling = this.canSchedule(patient);
      if (patient.city !== null && patient.state !== null) {
        this.patientLocation = `${this.patient.city}, ${this.patient.state}`;
      }

      if (this.patient.personaClientId == null && this.patient.accountInviteSentAt === 'Invalid Date' && this.patient.active) {
        this.status = 'not invited';
      } else if (this.patient.personaClientId == null && this.patient?.accountInviteSentAt === 'Invalid Date' && !this.patient?.active) {
        this.status = 'inactive';
      } else if (this.patient.personaClientId == null) {
        this.status = 'invited';
      } else if (this.patient.active) {
        this.status = 'client';
      } else if (!this.patient.active) {
        this.status = 'inactive';
      }

      this.checkEligibilityForRefer();
    });
  }

  canSchedule(patient: Patient): boolean {
    // ensure non-SPN patients can schedule but all others must have an account and completed their account
    if (patient.nonSpnPatient) {
      return patient.allowScheduling;
    }

    return patient.personaClientId && patient.allowScheduling && !patient.accountPending;
  }

  patientAdded(patient: Patient) {
    this.notificationService.showNotificationToast({
      message: 'New client created successfully!',
      type: ToastType.SUCCESS,
      duration: 5000
    });
    this.router.navigate(['clients', patient.id]);
  }

  get showPatientDocuments(): boolean {
    return this.appService.patientDocumentsFeatureGate;
  }

  get haveClientRecord(): Observable<boolean> {
    return this.clientDetailPageService.patient.pipe(map((p) => p && p.haveClientRecord && !p.accountPending));
  }

  get enableChatIcon(): boolean {
    return !!this.patient?.chatProfileUuid;
  }

  get clientId(): Observable<number> {
    return this.clientDetailPageService.patient.pipe(map((p) => p && p.id));
  }

  openChat(): void {
    if (this.actionListMenu() && this.actionListMenu().isOpen) {
      this.actionListMenu().toggleMenu();
    }
    if (this.enableChatIcon) {
      this.chatStore.openChatRoomByPlatformIdentity$(this.patient.platformIdentity).pipe(take(1)).subscribe();
    }
  }

  markClientActive(patient: Patient): void {
    if (this.actionListMenu() && this.actionListMenu().isOpen) {
      this.actionListMenu().toggleMenu();
    }
    this.confirmModal
      .openThen('Confirm Activation', 'Are you sure you want to make this client active?', 'Active')
      .pipe(
        filter((r) => !!r),
        switchMap(() => this._patientService.activeInactive(patient.userId, patient.id, true))
      )
      .subscribe((r) => {
        this.redirectToClients();
      });
  }

  markClientInactive(patient: Patient) {
    if (this.actionListMenu() && this.actionListMenu().isOpen) {
      this.actionListMenu().toggleMenu();
    }
    if (patient.canInactivate) {
      this.modalVisible = true;
    } else {
      this.notificationService.showNotificationToast({
        message: "Please submit your client's claim before deactivating the client.",
        type: ToastType.INFO,
        showCloseButton: true,
        autoClose: false
      });
      return;
    }

    this.clientTerminationReasonsModalService.setSelectedPatientId(patient.id);
    this.handleClientTerminationModal();
    this.clientTerminationReasonsModalService.subscribeToModalEvents().pipe(
      take(2)
    ).subscribe((status) => {
      if (status.clientActivation && status.clientActivation === ClientActivation.INACTIVE) {
        this._patientService.activeInactive(patient.userId, patient.id, false)
          .subscribe(() => {
            this.redirectToClients();
            this.modalVisible = false;
          });
      }
    });
  }

  redirectToClients(): void {
    this.router.navigateByUrl('/clients/current');
  }

  private handleClientTerminationModal(): void {
    if (this.actionListMenu() && this.actionListMenu().isOpen) {
      this.actionListMenu().toggleMenu();
    }
    this.clientTerminationReasonsModalService.toggleModal();
  }

  goBackToList(): void {
    const route = this.isMatchList ? '/clients/matches' : '/clients/current';
    if (this.isMatchList) {
      this.matchClientStore.selectedTab = 3;
    }
    this.router.navigate([route]);
  }

  goBack(): void {
    if (this.routeComingFrom) {
      this.router.navigate([this.routeComingFrom]);
      return;
    }
    this.goBackToList();
  }

  goToCalendarEvent(): void {
    this.router.navigate(['/scheduling', 'events', this.patient?.nextCalendarEventId]);
  }

  private openPsychiatryComponentModal(): void {
    if (this.modalOpen) return;

    this.modalOpen = true;

    const modalRef = this.irisModalService.openUnstyledFullScreenOverlay<PsychiatryModalComponent, PsychiatryModalInputType, null>(
      PsychiatryModalComponent,
      { patient: this.patient, provider: this.user, eligibilityData: this.eligibilityData }
    );

    modalRef.afterClosed$().pipe(
      take(1),
    ).subscribe((res) => {
      this.modalOpen = false;
      this.checkEligibilityForRefer();
    });
  }

  private redirectToTfaForm(): void {
    this.trackVaRedirect();
    window.open(this.tfaFormLink, '_blank');
  }

  private trackVaRedirect(): void {
    const referringType = this.eligibilityData.psychReferral
      ? InternalReferralTypes.THERAPY
      : InternalReferralTypes.PSYCHIATRY;

    // Segment Event
    this.segmentService.track<SondermindEvent.VaInternalReferralRedirectToTfaForms>(
      SondermindEvent.VaInternalReferralRedirectToTfaForms,
      {
        providerId: this.user.personaProviderId,
        providerName: this.user.fullName,
        providerEmail: this.user.email,
        referralName: this.patient.fullName,
        referralEmail: this.patient.email,
        referralPersonaClientId: this.patient.personaClientId,
        referringType,
        dateViewed: new Date().toISOString()
      }
    );

    // DataDog logger
    this.logger.info(
      'VA Internal Referral Redirect to TFA Forms',
      {
        type: 'create_internal_referral',
        event: 'internal_referral_redirect',
        persona_provider_id: this.user.personaProviderId,
        persona_client_id: this.patient.personaClientId,
        referring_type: referringType
      }
    );
  }

  handleReferToAction() {
    if (this.actionListMenu() && this.actionListMenu().isOpen) {
      this.actionListMenu().toggleMenu();
    }
    if (this.eligibilityData?.hasActiveReferral) {
      this.notificationService.showNotificationToast({
        message: 'This client has already been referred.',
        type: ToastType.INFO,
        showCloseButton: true,
        autoClose: false
      });
      return;
    }
    if (this.eligibilityData?.isNotEligibleForRefer) {
      this.notificationService.showNotificationToast({
        message: 'This client is not eligible for referral.',
        type: ToastType.INFO,
        showCloseButton: true,
        autoClose: false
      });
      return;
    }
    this.eligibilityData?.vaReferral ? this.redirectToTfaForm() : this.openPsychiatryComponentModal();
  }

  checkEligibilityForRefer(): void {
    this.internalReferralService.getEligibleForReferral(this.patient.personaClientId).pipe(
      takeUntil(this.destroyed$)
    ).subscribe((eligibilityObj) => {
      this.isLoading = false;
      this.eligibilityData = eligibilityObj;
      if (eligibilityObj?.validState) {
        this.displayReferButton$.next(true);
      } else {
        this.displayReferButton$.next(false);
      }

      if (eligibilityObj?.psychReferral) {
        this.tfaFormLink = InfoLinks.REFER_TO_THERAPY;
      }

      this.referToLabel = eligibilityObj?.psychReferral ? 'Refer to therapy' : 'Refer to psychiatrist';
      this.referToIcon = eligibilityObj?.psychReferral ? 'chair' : 'admin_meds_outline';
    });
  }

  onOutletAttached(event): void {
    // Hide the client header when a note is shown
    if ((event instanceof ClientDetailsSmartNotesComponent ||
        event instanceof ClientDetailTreatmentplanComponent) && event.route) {
      event.route.params
        .subscribe((params) => {
          this.showClientHeader = !params.note_id;
        });
    } else {
      this.showClientHeader = true;
    }
  }

  setMenuItems(): void {
    const menuItems: IMenuItemList[] = [];
    this.displayReferButton$.pipe(take(1)).subscribe((displayReferButton) => {
      if (displayReferButton) {
        menuItems.push({
          action: () => this.handleReferToAction(),
          dataTest: 'refer-to-psychiatry-menu-item',
          label: this.referToLabel,
          icon: this.referToIcon
        });
      }
    });
    if (this.enableChatIcon) {
      menuItems.push({
        label: 'Message client',
        action: () => this.openChat(),
        dataTest: 'message-client-menu-item',
        icon: 'chat_bubble_outline'
      });
    }
    if (this.patientActive) {
      menuItems.push({
        label: 'Mark as inactive',
        action: () => this.markClientInactive(this.patient),
        dataTest: 'mark-inactive-menu-item',
        icon: 'person_off',
        destructive: true
      });
    } else {
      menuItems.push({
        label: 'Mark as active',
        action: () => this.markClientActive(this.patient),
        dataTest: 'mark-active-menu-item',
        icon: 'person_check',
      });
    }
    this.menuItems.set(menuItems);
  }
}
